import React, {useEffect, useMemo, useRef, useState} from 'react';
import TemplateDefault from "../../components/template-default";
import Card from "../../components/card";
import Button from "../../components/button";
import {FaEraser, FaFileExcel, FaPlusSquare, FaRegStar, FaStar} from "react-icons/fa";
import {AgGridReact} from "ag-grid-react";
import {LocalizationAgGrid} from "../../utils/localization-ag-grid";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import softwareAttendanceService from "../../services/software-attendance-service";
import StatusCellRenderer from "./components/status-cell-renderer";
import SelectComponent from "../../components/select-component";
import {statusList} from "./resources/statusList";
import projectService from "../../services/project-service";
import userService from "../../services/user-service";
import InputForm from "../../components/input-form";
import InputDateTime from "../../components/input-datetime";
import TruncateCellRenderer from "../../components/cell-renderer/truncate-cell-renderer";
import general from "../../utils/general";
import IdRender from "./components/id-render";
import {toast} from "react-toastify";
import {AlertMessage} from "../../utils/alert-message";
import useFetchData from "../requests/hooks/use-fetch-data";
import InputCheckbox from "../../components/input-checkbox";

const defaultFilters = {
    status: [
        {value: 'ABERTO', label: 'Pendente suporte'},
        {value: 'CLIENTE', label: 'Pendente cliente'},
        {value: 'ENCERRADO', label: 'Encerrado (sem solução)'},
        {value: 'BLOQUEIO', label: 'Pendente comercial'},
        {value: 'SOLUCIONADO', label: 'Encerrado (solucionado)'},
    ],
    project: {value: "", label: "Todos"},
    responsible: "",
    last_update: "",
    searchData: "",
    group: [],
    has_request: false,
}

let initialFilters = defaultFilters;

const SoftwareAttendances = () => {
    const filter = localStorage.getItem("control_attendances_filters");
    if (filter) {
        const filterParsed = JSON.parse(filter);
        initialFilters = { ...defaultFilters, ...filterParsed };
    }

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [filters, setFilters] = useState(initialFilters);
    const [selectedInitDate, setSelectedInitDate] = useState(null);
    const [selectedEndDate, setSelectedEndDate] = useState(null);

    const gridRef = useRef();
    const defaultColDef = useMemo(() => ({ resizable: true }), []);
    const {groups} = useFetchData();

    const onCellClicked = (params) => {
        const {event, data, colDef} = params;

        if (colDef.headerName === "Favorito"){
            const formData = new FormData();
            formData.append("favorite", !data.favorite);

            softwareAttendanceService.editFavorite(formData, data.id).then(() => {
                refetch()
            }).catch((err) => {
                const { data: { message } } = err.response;
                toast.error(AlertMessage[message] ?? "Não foi possível salvar");
            })
        }else{
            event.ctrlKey && event.button === 0 ? window.open(`/software-attendances/form/${data.id}`) : navigate(`/software-attendances/form/${data.id}`);
        }
    }
    const handleSelect = (id, value) => setFilters(prev => ({...prev, [id]: value}));
    const handleInput = ({target: {id, value}}) => setFilters(prev => ({...prev, [id]: value}));
    const handleCheckBox = (e) => setFilters(prev => ({...prev, 'has_request': e.target.checked}));

    useEffect(() => {
        document.title = "SISTEMA INTERNO";
        if (selectedEndDate && selectedInitDate) {
            setFilters(prev => ({...prev, date_initial: selectedInitDate, date_end: selectedEndDate}));
        } else {
            setFilters(prev => ({...prev, date_initial: "", date_end: ""}));
        }
    }, [selectedInitDate, selectedEndDate]);

    const {refetch, data: attendanceResult} = useQuery({
        queryFn: () => {
            const newFilter = {
                status: filters.status,
                project: filters.project,
                responsible: filters.responsible,
                last_update: filters.last_update,
                group: filters.group,
                has_request: filters.has_request,
            }

            localStorage.setItem("control_attendances_filters", JSON.stringify(newFilter));

            return softwareAttendanceService.list(filters).then(({data}) => data)
        },
        queryKey: ['attendances', {filters}]
    });

    const attendances = attendanceResult?.rows ?? [];
    const lastRow = attendanceResult?.lastRow ?? 0;

    const {data: responsibles} = useQuery({
        queryFn: () => userService.getResponsibles().then(({data}) => {
            data.unshift({value: "", label: "Todos"});
            return data;
        }),
        queryKey: ['users']
    });

    const {data: projects} = useQuery({
        queryFn: () => projectService.options().then(({data}) => {
            data.unshift({value: "", label: "Todos"});
            return data;
        }),
        queryKey: ["projects"]
    });

    useEffect(() => {
        if (!searchParams.get("project-id")) return;

        const project = projects?.find(p => p.value.toString() === searchParams.get("project-id")) ?? null

        setFilters(prev => ({
            ...prev,
            project,
            status: [
                {value: 'ABERTO', label: 'Pendente suporte'},
                {value: 'CLIENTE', label: 'Pendente cliente'},
            ]
        }));
    }, [projects, searchParams]);

    const [columnDefs] = useState([
        {field: "id", headerName: "ID", width: 50, cellRenderer: IdRender},
        {field: "favorite", headerName: "Favorito", width: 30,
            headerComponent: () => (
                <div className={"flex justify-center w-full"}>
                    <FaStar style={{ color: '#FED328' }} />
                </div>
            ),
            cellRenderer: params => {
                return (
                    <div className={"flex justify-center w-full"}>
                        {
                            params.value
                                ? <FaStar style={{ color: '#FED328' }} />
                                : <FaRegStar />
                        }
                    </div>
                );
            }
        },
        {field: "dt_initial", headerName: "Abertura", width: 120,
            cellRenderer: params => {
                return general.formatDate(params.value)
            }
        },
        {field: "status", headerName: "Status", width: 170, cellRenderer: StatusCellRenderer},
        {field: "project", headerName: "Projeto", width: 193},
        {field: "requester", headerName: "Solicitante", width: 95, cellRenderer: TruncateCellRenderer},
        {field: "problem", headerName: "Descrição", flex: 1, cellRenderer: TruncateCellRenderer, width: 300},
        {
            field: "module_name", headerName: "Grupo", width: 150, cellRenderer: TruncateCellRenderer,
            valueGetter: params => {
                return general.prepareArrayToList(params.data.module_name);
            },
        },
        {field: "responsible", headerName: "Atendente resp."},
        {
            field: "has_request",
            headerName: "Solicitações",
            width: 90,
            cellRenderer: ({value}) => value ? "Sim" : "Não",
        },

    ]);

    const exportSoftware= () => {
        const newArray = attendances.map(data =>
            ({
                "ID": data.id,
                "Favorito": data.favorite ? "Sim" : "Não",
                "Abertura": general.formatDate(data.dt_initial),
                "Status": filterStatus(data.status),
                "Projeto": data.project,
                "Solicitante": data.requester,
                "Descrição": data.problem,
                "Grupo": general.prepareArrayToList(data.module_name),
                "Atendente resp.": data.responsible,
                "Solicitações": data.has_request ? "Sim" : "Não",
            })
        );

        general.exportCsv(newArray);
    }

    const filterStatus = (statusToFilter) => {
        const filtered = statusList.find(status =>
            status.value === statusToFilter.toUpperCase()
        )
        return filtered.label
    }

    const clearFields = () => {
        setFilters({
            status: [
                {value: 'ABERTO', label: 'Pendente suporte'},
                {value: 'CLIENTE', label: 'Pendente cliente'},
                {value: 'ENCERRADO', label: 'Encerrado (sem solução)'},
                {value: 'BLOQUEIO', label: 'Pendente comercial'},
                {value: 'SOLUCIONADO', label: 'Encerrado (solucionado)'},
            ],
            project: {value: "", label: "Todos"},
            responsible: "",
            last_update: "",
            searchData: "",
            group: [],
            has_request: false
        })
        setSelectedInitDate(null)
        setSelectedEndDate(null)
    }

    return (
        <TemplateDefault title={"Atendimentos"}>
            <Card>
                <div className="flex flex-col lg:grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-12">
                    <Button col="col-span-2" onClick={() => navigate("/software-attendances/form")}>
                        <FaPlusSquare/> Novo atendimento
                    </Button>

                    <div className={"col-span-2"}>
                        <InputDateTime id={"date-init"} label={"Data abertura (inicio)"} selected={selectedInitDate}
                                       onChange={date => setSelectedInitDate(date)} 
                                       showTime/>
                    </div>
                    <div className={"col-span-2"}>
                        <InputDateTime id={"date-end"} label={"Data abertura (fim)"} selected={selectedEndDate}
                                       onChange={date => setSelectedEndDate(date)} 
                                       showTime/>
                    </div>

                    <div className="col-span-3 text-gray-900">
                        <SelectComponent
                            instanceId="responsible"
                            label="Atendente"
                            options={responsibles ?? []}
                            value={filters.responsible}
                            onChange={(value) => handleSelect("responsible", value)}
                            name="responsible"
                        />
                    </div>

                    <div className="col-span-3 text-gray-900">
                        <SelectComponent
                            instanceId="last-update"
                            label="Última alteração"
                            options={responsibles ?? []}
                            value={filters.last_update}
                            onChange={(value) => handleSelect("last_update", value)}
                            name="last_update"
                        />
                    </div>

                    <div className="col-span-3 text-gray-900">
                        <SelectComponent
                            instanceId="projects"
                            label="Projeto"
                            options={projects ?? []}
                            value={filters.project}
                            onChange={(value) => handleSelect("project", value)}
                            name="projects"
                        />
                    </div>

                    <div className="col-span-3 text-gray-900">
                        <SelectComponent
                            isMulti
                            instanceId="status"
                            label="Status"
                            options={statusList}
                            value={filters.status}
                            onChange={(value) => handleSelect("status", value)}
                        />
                    </div>

                    <div className="col-span-6 flex gap-4 text-gray-900">
                        <SelectComponent
                            isMulti
                            instanceId="group"
                            label="Grupo"
                            options={groups ?? []}
                            value={filters.group}
                            onChange={(value) => handleSelect("group", value)}
                            name="group"
                            col={"w-full"}
                        />

                        <InputCheckbox id={"has_request"} name={"has_request"} label={"Solicitações"}
                                       value={filters.has_request}
                                       checked={filters.has_request}
                                       onChange={e => handleCheckBox(e)}
                        />
                    </div>



                    <div className="col-span-12">
                        <div className="flex gap-2">
                            <InputForm id="searchData" label="Pesquisar" col="w-full" type="search"
                                       value={filters.searchData}
                                       onChange={handleInput}/>
                            <div className={"flex gap-4"}>
                                <Button onClick={() => clearFields()} col="w-[60px]">
                                    <FaEraser/>
                                </Button>
                                <Button target="success" onClick={exportSoftware} col="!h-[32px] w-[60px]">
                                    <FaFileExcel/>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className={`${lastRow > 500 ? "h-[calc(100vh-301px)]" : "h-[calc(100vh-233px)]"} ag-theme-quartz grid mt-4`}>
                    <AgGridReact
                        ref={gridRef}
                        rowHeight={32}
                        rowData={attendances}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        pagination
                        paginationPageSize={100}
                        paginationPageSizeSelector={[20, 50, 100, 500]}
                        animateRows={false}
                        suppressRowClickSelection={true}
                        suppressCellFocus={true}
                        onCellClicked={onCellClicked}
                        gridOptions={{localeText: LocalizationAgGrid}}
                    />
                </div>
                {
                    lastRow > 500 &&
                    <div className="flex p-2 rounded-sm bg-[#FBF0DA] mt-2">
                        {`Limite de 500 registros atingido. Exibindo ${attendances.length} de ${lastRow}.`}
                    </div>
                }
            </Card>
        </TemplateDefault>
    );
};

export default SoftwareAttendances;
